import React from 'react';
import type { CardProps, CardVariant, CardPadding } from './types';

// ─── Variant class maps ───────────────────────────────────────────────────────

const VARIANT_CLASSES: Record<CardVariant, string> = {
  default: 'bg-neutral-100 shadow-sm',
  elevated: 'bg-white shadow-md',
  outlined: 'bg-white border border-neutral-200',
  interactive:
    'bg-white shadow-sm cursor-pointer transition-shadow duration-150 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
  status: 'bg-white shadow-sm border-l-4',
};

// ─── Padding class maps ───────────────────────────────────────────────────────

const PADDING_CLASSES: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

// ─── Card ─────────────────────────────────────────────────────────────────────

export function Card({
  variant = 'default',
  padding = 'md',
  children,
  onClick,
  statusColor,
  header,
  footer,
  state = 'default',
  className,
  'aria-label': ariaLabel,
}: CardProps) {
  const isInteractive = variant === 'interactive' || !!onClick;

  const variantClasses = VARIANT_CLASSES[variant];
  const paddingClass = PADDING_CLASSES[padding];

  const stateClasses =
    state === 'error' ? 'border-t-2 border-t-danger-500' : '';

  const classes = [
    'rounded-md',
    variantClasses,
    paddingClass,
    stateClasses,
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const inlineStyle: React.CSSProperties =
    variant === 'status' && statusColor
      ? { borderLeftColor: `var(--color-${statusColor}, ${statusColor})` }
      : {};

  const sharedProps = {
    className: classes,
    style: inlineStyle,
    'aria-label': ariaLabel,
  };

  const content = (
    <>
      {header && <div className="mb-3">{header}</div>}
      {children}
      {footer && <div className="mt-3">{footer}</div>}
    </>
  );

  if (isInteractive) {
    return (
      <div
        {...sharedProps}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && onClick) {
            e.preventDefault();
            onClick();
          }
        }}
      >
        {content}
      </div>
    );
  }

  return (
    <div {...sharedProps}>
      {content}
    </div>
  );
}
